import { ClientNinetailedExperience } from './ClientNinetailedExperience';
import { getNinetailedData } from 'libs/contentful/queries/ninetailed';
import { ComponentType } from 'react';
import { moduleRegistry, isModuleRegistryTypeName } from 'libs/experimentation/moduleRegistry';
import { PageSectionProps } from 'components/PageSection/types';
import PageSection from 'components/PageSection/PageSection';
import ClientPageSection from 'components/PageSection/ClientPageSection';
import { getNinetailedMode, shouldRenderBaseline } from 'libs/experimentation/requestContext';
import { ModuleProps, ModuleTypeName } from 'utils/modules';
import ClientWrapper from './ClientWrapper';
import { ContentfulModule } from 'libs/contentful/types';
import { NinetailedModuleData } from 'libs/contentful/queries/page/ninetailed';
import { filterValidExperiences } from 'utils/experiences';
import { isSectionModule } from 'utils/sectionModules';

interface NinetailedExperienceProps<T extends ContentfulModule> {
  entryId: string;
  entry?: T;
  hasExperiments?: boolean;
  ninetailedData?: NinetailedModuleData;
}

export const getPageSectionModule = async (): Promise<ComponentType<PageSectionProps>> => {
  return (await getNinetailedMode()) === 'client' ? ClientPageSection : PageSection;
};

const getModule = async (moduleName: string): Promise<ComponentType<PageSectionProps> | undefined> => {
  if (moduleName === 'PageSection') {
    return getPageSectionModule();
  }
  if (!isModuleRegistryTypeName(moduleName)) {
    return undefined;
  }
  return moduleRegistry[moduleName] as ComponentType<PageSectionProps>;
};

/**
 * Client-mode (Contentful live-preview) wrapper for Ninetailed experiments.
 * Server-side variant resolution is handled by `NinetailedExperimentAdapter`
 * in the v2 pipeline; this component is only invoked by
 * `ModuleCollectionRenderer` when `ninetailedMode === 'client'`, so the
 * Ninetailed JS plugin can swap variants client-side.
 */
export const NinetailedExperience = async <T extends ContentfulModule>({
  entryId,
  entry,
  hasExperiments = false,
  ninetailedData,
}: NinetailedExperienceProps<T>) => {
  const moduleName = entry?.__typename || '';

  // Use pre-fetched data if available, otherwise fetch it
  const { baselineEntry, mappedExperiences } = ninetailedData || (await getNinetailedData(entryId, moduleName));

  if (!baselineEntry || !baselineEntry.sys?.id) {
    console.error(`Baseline entry not found or missing sys.id for entryId: ${entryId}`);
    return null;
  }

  const baselineEntryProps = baselineEntry as unknown as PageSectionProps & ModuleProps[ModuleTypeName];
  const experiences = filterValidExperiences(mappedExperiences);

  const finalProps =
    moduleName === 'PageSection' ? baselineEntryProps : isSectionModule(moduleName) ? entry : baselineEntryProps;

  const Module = await getModule(moduleName);

  if (!Module) {
    console.error(`Module ${moduleName} not found in module registry`);
    return null;
  }

  if (!hasExperiments || shouldRenderBaseline()) {
    return <Module {...(finalProps as unknown as PageSectionProps & ModuleProps[ModuleTypeName])} />;
  }

  if (moduleName === 'PageSection') {
    return (
      <ClientNinetailedExperience
        id={baselineEntry.sys.id}
        module={Module}
        passthroughProps={baselineEntryProps}
        experiences={experiences}
        hasExperiments={hasExperiments}
      />
    );
  }

  return (
    <ClientWrapper
      id={baselineEntry.sys.id}
      moduleType={moduleName as ModuleTypeName}
      passthroughProps={finalProps as unknown as PageSectionProps & ModuleProps[ModuleTypeName]}
      experiences={experiences}
      hasExperiments={hasExperiments}
    />
  );
};
